/*
 * 戒导航 · 分享模块（jiedh/share）前台逻辑入口
 * =============================================================================
 * 设计依据：doc/分享功能设计方案.md
 *   · §8.4.2「序列化形态」—— 前台**不逐个读设置键**，统一读聚合对象
 *     `app.forum.attribute('jiedhShareConfig')`（服务端由 ForumShareConfig 产出）。
 *   · §12.1 P0 交付 —— P0 前台**零可见 UI**：只把配置读进来，不挂载任何控件。
 *   · §6.1.3「入口清单」—— P1 在此挂载三处入口：
 *       ① 讨论页顶部控件区（下拉项，含游客）
 *       ② 每个楼层右下控件区（下拉项，含游客）
 *       ③ 博客文章正文下方（横向常驻分享条，priority 90）
 *     三处都必须先过 `enabled` 这道总开关 —— §8.4.2 第 2 条明确要求
 *     「`enabled: false` 时前端**不注册任何入口**」。
 *   · §6.1.1 目录树 —— 前台真实逻辑放这里，`js/forum.ts` 只做转发。
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ★ 本文件为什么从 `index.ts` 改名成 `index.tsx`（2.x 实施期裁决，2026-09-25）
 * ─────────────────────────────────────────────────────────────────────────────
 * P1 的三处入口都是「往 ItemList 里塞一个 JSX 节点」。而本项目自定义扩展的
 * 源码目录里 `.ts` 会被 flarum-webpack-config 的 preset-typescript **按纯 TS 处理**，
 * `<ShareButton ... />` 这种尖括号语法会被当成**类型断言**从而解析失败。
 * `.tsx` 是唯一不需要额外 babel 配置的解法（`resolve.extensions` 本身已含 `.tsx`，
 * 见 packages/media/js/src/forum/index.tsx 的同款先例），改名零连带影响。
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ★ 主包体积红线：本文件**只能**静态依赖「按钮 + 配置」，其余一律懒加载
 * ─────────────────────────────────────────────────────────────────────────────
 * §6.4.3 chunk 表规定：主包（随首屏）含 gzip ≤ 2 KB，只允许出现
 * 「ShareButton + 三处 extend 挂载注册」。因此本文件：
 *   · 只 import 类型（`import type`）—— babel 整行擦除，零体积；
 *   · **不**静态 import `utils/text.ts`（700+ 行，属 panel chunk）——
 *     哪怕只是想借一个 `floorLabel()`，也会把整份文本逻辑拖进首屏。
 *     为此下面自建了 3 个 6 行以内的极小工具（toAbsolute / floorLabel / tagsOf），
 *     与 `utils/text.ts` 的同名能力构成**有意为之的轻微重复**，理由就是这个预算。
 *   · 面板本体（SharePanelModal）走 ShareButton 内部的动态 chunk，本文件看不到它。
 *
 * ⚠️ 关于 JSX 的写法约定（同 packages/icon-picker、packages/nav、packages/media）：
 *   本项目扩展**不 import mithril**，JSX 由 flarum-webpack-config 以 `pragma: 'm'`
 *   编译为对全局标识符 `m` 的调用。一旦 import mithril 会打包出第二份 mithril，
 *   造成 vnode 体系不一致（`import type Mithril` 只取类型，不在此列）。
 *
 * ⚠️ 关于「核心模块 import」的一个关键前提（已实证，见 packages/login-guard 的同款踩坑注释）：
 *   flarum-webpack-config 把 `import X from 'flarum/...'` 编译成
 *   `flarum.reg.get('core', '...')`（externals）。这对**主包内**的核心模块没问题，
 *   但对**核心的懒加载 chunk**（如 LogInModal / PostStream）会返回 undefined。
 *   本文件只需要 `DiscussionControls` / `PostControls` / `DiscussionPage` 三个——
 *   已核对 `vendor/flarum/core/js/dist/forum.js` 中三者均已注册（非独立 chunk，
 *   即都在核心主包内），所以静态 import 是安全的。
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ★ 前端的取值口径：一律「缺省即兜底」，绝不依赖字段存在
 * ─────────────────────────────────────────────────────────────────────────────
 * §8.4.2 契约要点④ 规定「字段只增不改」⇒ 后续期次会**只追加**字段。
 * 反过来，前端也必须容忍字段缺失/为 null（例如扩展被降级、缓存未刷新）。
 * 因此这里不做任何「字段必须存在否则抛错」的强校验，只做安全降级。
 */
import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import PostControls from 'flarum/forum/utils/PostControls';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import ShareButton from './components/ShareButton';

// 以下全部是**类型导入**：babel 转译时整行擦除，不产生任何运行时依赖。

// ★ 纯类型导入（babel 整行擦除）—— 只为编辑器里的类型提示，不产生任何运行时依赖。

/*
 * 契约字段版本号 —— 必须与 PHP 侧 JieDH\Share\ForumShareConfig::VERSION 保持一致
 * （§19.6 R7：该字段由原设计的 qrVersion 改名而来，语义是「前端契约版本」，
 *  与二维码无关；仅在**破坏性**调整既有字段时才 +1）。
 */
export const CONFIG_VERSION = '1';

/** 品牌信息（海报卡片水印区，P1 使用） */

/** 合法色卡 key（§7.3.1 四套色卡，顺序即 A/B/C/D；与 PHP 侧 ForumShareConfig::PALETTES 严格一致） */

/** 色卡 key 白名单 —— 与 palettes.ts 的 PALETTE_KEYS 同源口径，此处只做最小兜底校验 */
const PALETTE_KEYS = ['A', 'B', 'C', 'D'];

/**
 * `jiedhShareConfig` 的完整形态（§8.4.2 契约 + P1 五项扩展）
 *
 * ★ P1 新增的 5 个字段与 PHP 侧 ForumShareConfig::__invoke() 的输出**一一对应**：
 *     enableQr       ← jiedh-share.enable_qr         （二维码渠道是否存在）
 *     showQr         ← jiedh-share.default_show_qr   （海报默认是否含二维码，仅决定初始态）
 *     palette        ← jiedh-share.default_palette   （默认色卡 A/B/C/D）
 *     channels       ← jiedh-share.channels          （启用的渠道 id 列表；[] = 管理员明确全关）
 *     quoteMaxLines  ← jiedh-share.quote_max_lines   （金句最大行数 1~8）
 *
 * ★ P2 新增的 2 个字段（见 §8.4.1 / §8.4.2 / §6.8.3）：
 *     enableHome     ← jiedh-share.enable_home       （首页精简分享是否启用）
 *     tags           ← jiedh-share.tags              （话题标签基线；**wire 形态是 string**）
 *
 * ★ 刻意**没有**的字段（不要照 §8.4.2 的旧示例补回来）：
 *     · `qrApi` / `qrVersion` → §19.6 Q2（二维码改前端生成）+ R7（改名 configVersion）已永久作废。
 */

/** 分享评价审核开关（见 ShareConfig.remarkReview） */

/**
 * 读取并规整前台分享配置。
 *
 * 返回值保证「结构完整、类型正确」，调用方无需再做空值判断：
 *   · 属性整体缺失（扩展未生效 / 缓存陈旧）→ 回落为「不启用」的保守形态；
 *   · 字段个别缺失 → 各自回落（`enabled` 缺省视为 true，与 PHP 侧默认值一致）。
 *
 * ★ 兜底方向必须与 PHP 侧 ForumShareConfig 保持一致，否则会出现
 *   「PHP 认为该开、前端认为该关」的诡异表现（P1 复核时逐一对照过）：
 *   · `enabled` / `enableQr` / `showQr`：用 `!== false`，让「字段缺失」落到「开」；
 *   · `palette`：非白名单值一律回退 `'A'`（PHP 侧同样回退 'A'）；
 *   · `channels`：非数组回退 `[]`。⚠️ 注意这里**不能**把 `[]` 再兜成全开：
 *     PHP 侧明确区分「空数组 = 管理员全关」与「键未设置 = 全开」，
 *     若在前端把 `[]` 兜成全开，就会让管理员的「全关」意图失效。
 *     「键未设置」那种情况 PHP 已经输出成完整 12 项，前端看不到空数组之外的歧义。
 *   · `quoteMaxLines`：clamp 1~8（非数字 → 落到下限 1）。
 *   · `enableHome`（P2）：用 `!== false`，与 PHP 侧 `enable_home` 默认 true 同向；
 *   · `tags`（P2）：wire 是字符串，这里切分成 `string[]`（见 `parseHashtags()`）。
 *     ⚠️ 空串必须解析成 `[]`（=「一键关闭标签」，§6.8.3），**不能**兜回默认值。
 */
export function shareConfig() {
  const raw = app.forum.attribute('jiedhShareConfig') ?? {};
  const brand = raw.brand ?? {};
  return {
    // PHP 侧把布尔设置读成 '1'/'0' 再强转 bool，经 JSON 序列化后本身就是 true/false；
    // 这里用 `!== false`（而不是 Boolean(...)）是为了让「字段缺失」也落到「启用」，
    // 与 PHP 侧 get('jiedh-share.enable', true) 的兜底方向一致。
    enabled: raw.enabled !== false,
    // 二维码渠道是否存在（§8.4.2 契约要点③：二维码格显示条件 = enableQr && channels 含 'qrcode'）
    enableQr: raw.enableQr !== false,
    // 海报默认是否含二维码（面板内用户仍可临时切换，此项只决定初始态）
    showQr: raw.showQr !== false,
    // 默认色卡：非白名单值（含类型不对）一律回退 'A'
    palette: PALETTE_KEYS.indexOf(raw.palette) >= 0 ? raw.palette : 'A',
    // 启用渠道列表：非数组回退 []（见上方注释，绝不在前端把 [] 兜成全开）
    channels: Array.isArray(raw.channels) ? raw.channels.filter(id => typeof id === 'string') : [],
    // 金句最大行数：clamp 1~8，与 PHP 侧 quoteMaxLines() 完全同口径
    quoteMaxLines: clampQuoteMaxLines(raw.quoteMaxLines),
    brand: {
      // 品牌名/标语为空时**不在这里兜底**：真正的兜底在 PHP 侧 SiteMeta（站点名 / 默认标语）。
      // 前端若也写一份兜底文案，就会出现「两处文案不一致」的维护陷阱。
      name: typeof brand.name === 'string' ? brand.name : '',
      slogan: typeof brand.slogan === 'string' ? brand.slogan : ''
    },
    configVersion: typeof raw.configVersion === 'string' ? raw.configVersion : '',
    // 首页精简分享开关（P2）：缺省落到「开」，与 PHP 侧 jiedh-share.enable_home 默认 true 同向
    enableHome: raw.enableHome !== false,
    // 话题标签基线（P2）：wire 是字符串，此处切分为 string[]（空串 ⇒ [] = 一键关闭）
    tags: parseHashtags(raw.tags),
    // 分享评价审核开关（v1.3）：**严格**判等 true，缺失即 false（保守兜底，见接口注释）。
    remarkReview: {
      enabled: raw.remarkReview?.enabled === true
    }
  };
}

/**
 * 把 `jiedh-share.tags` 的 **wire 字符串**切分成归一化的标签数组（P2，§6.8.3）。
 *
 * 输入约定（§8.4.2 契约示例）：`'#戒导航 #读书笔记'` —— 空格分隔、可带 `#` 前缀。
 * 输出约定：`['戒导航','读书笔记']` —— **不带 `#`**（渲染时由 `formatHashtags()` 统一加），
 * 已去重（大小写不敏感，保留首次出现的写法）。
 *
 * ★ 三种输入的处理（与 `ShareBrand.tags` 注释 / `buildShareTarget()` 的三态严格对齐）：
 *   · 非字符串（字段缺失 / 类型不对，如陈旧缓存里是数组）⇒ 回退 PHP 侧默认值
 *     `['戒导航','读书笔记']`（= `jiedh-share.tags` 的 `default()`），
 *     使「老缓存」与「新装」表现一致；
 *   · 空串 / 纯空白 ⇒ `[]` —— **这是管理员的「一键关闭标签」意图，绝不能兜回默认值**
 *     （否则 §6.8.3「可留空关闭」的承诺永远无法兑现）；
 *   · 其余 ⇒ 按空白切分 → 去首尾 `#` → 去空串 → 去重。
 */
function parseHashtags(value) {
  if (typeof value !== 'string') return ['戒导航', '读书笔记'];
  const list = [];
  const seen = new Set();
  value.split(/\s+/).map(item => item.replace(/^#+/, '').replace(/#+$/, '').trim()).forEach(item => {
    if (!item) return;
    const key = item.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    list.push(item);
  });
  return list;
}

/**
 * 金句最大行数兜底 —— 与 PHP `ForumShareConfig::quoteMaxLines()` 同口径（clamp 1~8）。
 *
 * 区间依据见 PHP 侧注释：A 档金句区 ≈ 5 行；上限 8 是防御性留白。
 * 非数字（NaN / undefined / 字符串）统一落到下限 1，保证返回的一定是可用整数。
 */
function clampQuoteMaxLines(value) {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return 5; // 字段缺失（PHP 正常应给 5）→ 与 PHP 默认值一致
  return Math.max(1, Math.min(8, Math.floor(n)));
}

/* ═══════════════════════════════════════════════════════════════════════════
 * P1 第一节：极小语义工具（为避免把 utils/text.ts 拖进主包而就地重写）
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * 相对 URL → 绝对 URL（§6.2.3：分享链接必须是绝对 URL，否则抖音/微博抓不到内容）。
 *
 * ★ 为什么不用 `utils/text.ts` 的 `absoluteUrl()`：
 *   本文件属**主包**（≤ 2 KB gzip，§6.4.3），而 `utils/text.ts` 是 panel chunk 的组成部分
 *   （金句抽取 + 文案模板 + 标签组装，700+ 行）。一旦静态 import 它，webpack 会把整份
 *   文本逻辑提升进首屏主包，直接击穿体积红线。所以这里只重写这 6 行。
 *   两处口径**必须保持一致**：已是 `http(s)://` 的原样返回（不重新序列化，避免改写既有编码），
 *   否则以 `baseUrl` 为基准解析；没有 base 或解析失败时原样返回（宁可给相对链接，也不给空串）。
 *
 * @param url 通常是 `app.route(...)` 的返回值（**相对路径**，已含 basePath 前缀）。
 */
function toAbsolute(url) {
  const raw = String(url == null ? '' : url).trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  const base = String(app.forum.attribute('baseUrl') ?? '').trim();
  if (!base) return raw;
  try {
    return new URL(raw, base).toString();
  } catch {
    // baseUrl 不是合法绝对 URL（理论上不会发生）→ 退回相对链接
    return raw;
  }
}

/**
 * 楼层号 → 中文楼层标签（`第 3 楼`）。
 *
 * 用于 §6.8.1「post 类型的标题 = `{讨论标题} · 第 N 楼`」。非法值统一回落 `本楼`，
 * 这样它可以被直接拼进模板而不会产出「第 NaN 楼」这种脏文案。
 *
 * ★ 与 `utils/text.ts` 的 `floorLabel()` 同口径（同样的原因：不能为了一个函数拖进整个模块）。
 *   §6.2.2 R6 另有兜底：`position` 为空时 `buildShareTarget()` 会给 post 补「本楼」、
 *   给 discussion 补「第 1 楼」，所以即使这里不传也不会出现空文案。
 */
function floorLabel(n) {
  const num = Math.floor(Number(n));
  return Number.isFinite(num) && num > 0 ? `第 ${num} 楼` : '本楼';
}

/**
 * 取「模型上的标签 slug 列表」（§6.8.3：`buildShareTarget()` 取前 2 个拼话题标签）。
 *
 * ★ 为什么要写得这么防御：
 *   1. `tags()` 由 flarum/tags 通过 `extend(Discussion, 'tags')` 挂上，站点若未启用该扩展
 *      这个方法根本不存在 → 先判 `typeof === 'function'`；
 *   2. 关系**未 include 时 `tags()` 会返回 `false` 而不是空数组**
 *      （fof/blog 在 discussionRouting.ts 里专门为此写了注释，对应其 issue #170）
 *      → 必须 `Array.isArray()` 判一次，不能直接 `.map()`；
 *   3. 返回的 Tag 模型同样可能只有 id 没有 slug → 逐个取值并过滤空串。
 *
 * 入参故意收窄成 `unknown`：调用方可能传 Discussion、也可能是 `false`（关系未加载）。
 */
function tagsOf(discussion) {
  if (!discussion) return [];
  const getter = discussion.tags;
  if (typeof getter !== 'function') return [];
  let tags;
  try {
    tags = getter.call(discussion);
  } catch {
    return []; // 模型状态异常时不值得让整个入口挂掉
  }
  if (!Array.isArray(tags)) return [];
  return tags.map(tag => {
    const slug = tag && typeof tag.slug === 'function' ? tag.slug() : '';
    return typeof slug === 'string' ? slug : '';
  }).filter(Boolean);
}

/** 安全取「帖子正文 HTML」：`firstPost()` / `contentHtml()` 都可能因关系未加载而缺位。 */
function postHtml(post) {
  if (!post) return '';
  const getter = post.contentHtml;
  return typeof getter === 'function' ? String(getter.call(post) ?? '') : '';
}

/** 安全取「显示名」：`user()` 在关系未加载时返回 `false`（隐私口径见 §6.3.1，只输出显示名）。 */
function displayNameOf(user) {
  if (!user) return '';
  const getter = user.displayName;
  return typeof getter === 'function' ? String(getter.call(user) ?? '') : '';
}

/* ═══════════════════════════════════════════════════════════════════════════
 * P1 第二节：三处入口的「原料构造器」（§6.2.1 四类分享对象中的前三类）
 * ═══════════════════════════════════════════════════════════════════════════
 * 约定（与 ShareButton 的 attrs 契约严格对齐）：
 *   · 本函数**只在点击那一刻**被调用（延迟取值），因此这里读到的永远是最新模型状态；
 *   · **不做任何网络请求**：全部数据取自页面上已加载的模型与 forum 属性 —— 分享是
 *     零延迟操作，绝不能因为一次 API 往返让用户看到 loading；
 *   · 返回 `null` 表示「此刻不可分享」，ShareButton 会记一条 warn 并安静返回（不弹空面板）；
 *   · **一律不传 `image`**：P1 面板不渲染封面图（SharePanelModal 内的既有裁决），
 *     所以也没必要去正文里扒首图；
 *   · **一律不自己拼 `?ref=share`**：那由 `buildShareTarget()` 内部统一追加（§6.2.3），
 *     调用方只负责给「干净的 canonical 绝对 URL」。
 */

/* ═══════════════════════════════════════════════════════════════════════════
 * 隐藏态门禁：被隐藏的帖子 / 博客文章不再允许分享（2026-10-02 增补）
 * ═══════════════════════════════════════════════════════════════════════════
 * 背景：统一审核中心「隐藏」帖子后，首帖被隐藏会**联动把整篇讨论也置为隐藏**
 *   （packages/review 的 HideDiscussionWhenFirstPostHidden 监听器，见 doc/
 *   功能问题排查记录.md FQ-074）。此后**作者本人**与持 `discussion.hide` 权限的
 *   管理/版主仍能看到该内容（隐藏讨论只对普通用户过滤，见 vendor/flarum/core/
 *   src/Discussion/Access/ScopeDiscussionVisibility.php），但此时再给出「分享」入口
 *   是错误的 —— 被下架的内容不该再被扩散出去（用户 2026-10-02 反馈）。
 *
 * 判定口径：一律读核心模型自带的 `isHidden()`（由 `hiddenAt` 计算得出，Discussion
 *   与 Post 两个模型均有该实例方法），**不自己解析时间字段**。
 *
 * 覆盖范围（两道闸）：
 *   ① **渲染门禁** —— 三处入口在隐藏态下不 `items.add`，按钮根本不出现
 *      （避免像正文页投票图标那样留下「看得见却点不动」的死按钮）；
 *   ② **取值门禁** —— 三个 source 构造器对隐藏对象返回 `null`。这一道是为封住
 *      **正文划选浮动气泡**这条独立分享通路（它走 `sourceFromElement()` →
 *      `postSource()/blogSource()`，不经过 ① 的控件挂载点，见 §22.8）；
 *      气泡对 null 源本就静默不弹，无需额外改动。
 */
function isHiddenEntity(target) {
  return !!target && typeof target.isHidden === 'function' && target.isHidden() === true;
}

/**
 * 讨论页对象（§6.2.1 `discussion`）。
 *
 * URL 口径：用核心官方助手 `app.route.discussion()`（= `app.route('discussion', { id: slug() })`），
 * **不要自己拼 `/d/...`**。原因：
 *   · `Discussion.slug()` 返回的是 `id-slug` 组合串，正是 `:id` 路径参数要的东西；
 *   · fof/blog 会**覆写** `app.route.discussion`（见 vendor/fof/blog/js/src/forum/utils/discussionRouting.ts），
 *     把带博客标签的讨论重定向到 `/blog/{id}`。走官方助手能自动获得正确 URL，
 *     自己拼路径则会指向一个被重定向（甚至 404）的地址。
 */
function discussionSource(discussion) {
  if (!discussion) return null;

  // 隐藏态门禁：被隐藏的讨论不再可分享（见 isHiddenEntity 说明）
  if (isHiddenEntity(discussion)) return null;
  return {
    kind: 'discussion',
    canonicalUrl: toAbsolute(app.route.discussion(discussion)),
    title: discussion.title() ?? '',
    // 摘要与金句都取自首帖（§6.8.1 的 hook 口径）
    contentHtml: postHtml(discussion.firstPost()),
    author: displayNameOf(discussion.user()),
    replyCount: discussion.replyCount(),
    tags: tagsOf(discussion)
  };
}

/**
 * 楼层对象（§6.2.1 `post`）—— 用于每楼右下控件区的「分享本帖」。
 *
 * 与 discussion 的差别：
 *   · 标题要带楼层（`{讨论标题} · 第 N 楼`，§6.8.1），所以必须传 `position`；
 *   · 正文/作者取**本楼**的，不是首帖的；
 *   · 标签沿用所属讨论的（帖子本身没有标签）。
 *
 * URL 口径同官方助手 `app.route.post()`（其内部即 `app.route.discussion(post.discussion(), post.number())`）。
 */
function postSource(post) {
  if (!post) return null;

  // 隐藏态门禁：本帖被隐藏、或所属讨论被整篇隐藏，都不再可分享（见 isHiddenEntity 说明）
  if (isHiddenEntity(post) || isHiddenEntity(post.discussion())) return null;
  const discussion = post.discussion();
  return {
    kind: 'post',
    canonicalUrl: toAbsolute(app.route.post(post)),
    title: discussion ? discussion.title() ?? '' : '',
    contentHtml: postHtml(post),
    author: displayNameOf(post.user()),
    position: floorLabel(post.number()),
    tags: tagsOf(discussion)
  };
}

/**
 * 博客文章对象（§6.2.1 `blogPost`）。
 *
 * ★ 两处容易踩错的地方（2.x 实施期已实测核对）：
 *   1. **前端路由名是 `blogArticle`**（`/blog/:id`，见 vendor/fof/blog/js/src/forum/extend.ts 的
 *      `new Extend.Routes().add('blogArticle', '/blog/:id', BlogItem)`）。
 *      设计文档里出现的 `blog.post` 是 **PHP 侧** `Extend\Frontend->route()` 的路由名
 *      （vendor/fof/blog/extend.php），两者同名不同层，别混用。
 *   2. `app.route(name, params)` 会把 `params` 里除路径参数外的**所有键拼成查询串**
 *      （见 Application.route() 实现：先填 `:key`，再 `m.buildQueryString(剩余 params)`）。
 *      所以这里**只能传 `id`**，绝不能顺手塞业务字段，否则链接会多出 `?foo=bar`。
 *
 * body 口径：`blogMeta.summary`（§6.2.1 表格：博客摘要优先于正文截断）→ 取不到则留空，
 * 由 `buildShareTarget()` 自动回落到首帖正文截断。
 */
function blogSource(article) {
  if (!article) return null;

  // 隐藏态门禁：被隐藏的博客文章不再可分享（见 isHiddenEntity 说明）
  if (isHiddenEntity(article)) return null;
  const meta = typeof article.blogMeta === 'function' ? article.blogMeta() : null;
  const summary = meta && typeof meta.summary === 'function' ? String(meta.summary() ?? '') : '';
  return {
    kind: 'blogPost',
    canonicalUrl: toAbsolute(app.route('blogArticle', {
      id: article.slug()
    })),
    title: article.title() ?? '',
    contentHtml: postHtml(article.firstPost()),
    summary,
    author: displayNameOf(article.user()),
    tags: tagsOf(article)
  };
}

/**
 * fof/blog 文章页实例所需的最小结构（只取分享用得到的字段）。
 *
 * 刻意**不** import BlogItem 的真实类型：那是 `ext:` 惰性模块（拿不到也不必拿），
 * 且这样写也避免了与 fof/blog 的类型强耦合 —— 升级它时坏掉的只会是类型提示，
 * 不会是运行逻辑（下面每个取值都有 `typeof === 'function'` 守卫）。
 *
 * 说明：文章本身就是 `Discussion` 模型（fof/blog 的 `BlogItem.article` 类型即
 * `ApiResponseSingle<Discussion>`），`blogMeta` 关系由 fof/blog 通过
 * `new Extend.Model(Discussion).hasOne<BlogMeta>('blogMeta')` 挂上。
 */

/** fof/blog 文章页实例的最小结构（`extend(字符串路径, ...)` 的回调 `this`）。 */

/* ═══════════════════════════════════════════════════════════════════════════
 * P1 第三节：挂载三处入口（§6.1.3）
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * 三处分享入口的实际挂载逻辑（§6.1.3）。
 *
 * ★★ 为什么必须由 `app.beforeMount()` 调用，而不能直接写在 initializer 体内 ★★
 *   Flarum 2.x 的 `Application.boot()` 执行顺序是：
 *     `initialize()`（跑完全部 initializers）→ `store.pushPayload()` →
 *     **`app.forum = ...`** → **`app.session = ...`** → `runBeforeMount()` → `mount()`
 *   ⇒ **initializer 运行时 `app.forum` / `app.session` 还不存在**。
 *
 *   本扩展最初把 `shareConfig()`（内部读 `app.forum.attribute`）直接写在 initializer 顶层，
 *   结果第一句就抛 `TypeError: Cannot read properties of undefined (reading 'attribute')`，
 *   且该异常被 `initialize()` 的 try/catch 捕获后**推迟到 boot 末尾才报**，
 *   表现为「控制台零报错、页面零渲染」——三处入口一个都没挂上，极难排查。
 *
 *   正解：把「需要 forum/session 的逻辑」放进 `app.beforeMount()`。该回调在
 *   `runBeforeMount()` 中执行（此时 forum/session 已就绪、且早于首次 `mount()`），
 *   并且核心在跑完后会清空队列（**一次性**），不会重复执行。
 *   源码依据：vendor/flarum/core/js/src/common/Application.tsx L377-397（boot）、
 *   L431-438（beforeMount / runBeforeMount）。
 *
 *   保持 `enabled` 总开关「关闭即不注册任何入口」的语义（§8.4.2 第 2 条）——
 *   若改为「先无条件注册 extend，再在回调里判断 enabled」，虽然视觉结果相同，
 *   但会偏离该条设计约定，故不采用。
 */
function mountShareEntries() {
  const config = shareConfig();

  // 仅在后端开启 debug 时打印（生产环境保持控制台干净，符合项目「关键日志」而非「噪音日志」的约定）
  if (app.forum.attribute('debug')) {
    console.log('[jiedh-share] 前台配置', config);
  }

  /*
   * 契约自检：属性整体缺失说明 PHP 侧没把 jiedhShareConfig 挂上论坛 payload，
   * 这一定是配置/安装问题（常见原因：根 composer.json 未 require jiedh/share、
   * 或 js/dist 未重新构建）。此处用 warn 而非 error：功能会优雅降级，不至于中断。
   */
  if (app.forum.attribute('jiedhShareConfig') === undefined) {
    console.warn('[jiedh-share] 未读到 jiedhShareConfig 属性：前台分享功能将不可用。请检查扩展是否已启用、前端产物是否已重新构建。');
    return;
  }

  // 总开关关闭 ⇒ 前台不挂载任何分享入口（§8.4.2 第 2 条：不注册任何入口，而不是「注册了再隐藏」）
  if (!config.enabled) {
    return;
  }

  /*
   * ★ 2026-10-02（戒打卡分享重设计 §4.1.3 / §4.1.4）：注册跨扩展公开 API。
   *
   * 位置刻意在**两道守卫之后**：只有「扩展已生效且总开关开启」时才对外暴露能力，
   * 这样戒打卡侧 `window.jiedhShare?.can('open')` 为假时自然回退到旧的
   * Web Share + 剪贴板路径（§4.2.2 的降级分支），无需它自己判断 share 的开关。
   */
  registerJiedhShareApi(config);

  /* ─────────────────────────────────────────────────────────────────────────
   * 入口 ①：讨论页顶部控件区（`DiscussionControls.userControls`）
   * ─────────────────────────────────────────────────────────────────────────
   * ★ 2.x 写法要点：`DiscussionControls` 是 `const DiscussionControls = { ... }` 的
   *   **对象字面量**（见 vendor/flarum/core/js/src/forum/utils/DiscussionControls.js L14），
   *   没有 prototype。设计文档 §6.1.3 表格里写的扩展目标是 `DiscussionControls.prototype`
   *   在 2.x 下**是错的**（会拿到 undefined 的 prototype 而静默无效），这里直接传对象。
   *   （文档偏差已登记，p1-10 回写设计文档。）
   *
   * ★ 回调第一参数是**原方法的返回值**（2.x 的 extend 会先自动调用原方法再把返回值传进来），
   *   所以这里绝不能再 `original.call(...)`，直接往 `items` 里 add 即可。
   *
   * ★ 为什么必须有 `context instanceof DiscussionPage` 守卫：
   *   讨论**列表**的每一行（DiscussionListItem）也会调用 `DiscussionControls.controls()`
   *   来渲染行内下拉菜单。若不守卫，分享项会污染全站每一个讨论列表行的下拉菜单。
   *   核心自己对 `reply` 项用的就是同一条守卫（DiscussionControls.js L53），照抄同源写法。
   * ───────────────────────────────────────────────────────────────────────── */
  extend(DiscussionControls, 'userControls', function (items, discussion, context) {
    if (!(context instanceof DiscussionPage)) return;

    /*
     * 渲染门禁（2026-10-02）：讨论被隐藏（或被下架审核）时不挂分享项 ——
     * 详见文件内 isHiddenEntity() 注释。此门禁对**作者本人与版主**同样生效：
     * 他们能看到隐藏讨论，但不该再分享出去。
     */
    if (isHiddenEntity(discussion)) return;

    /*
     * priority -10：核心的「回复」项用的是默认优先级 0，负值让「分享」排在它**之后**。
     * 分享是低频操作，不该抢在最高频的「回复」前面（ItemList 按 priority 降序排列）。
     */
    items.add('jiedhShare', m(ShareButton, {
      resolve: () => discussionSource(discussion),
      config: config
    }), -10);
  });

  /* ─────────────────────────────────────────────────────────────────────────
   * 入口 ②：每个楼层右下控件区（`PostControls.userControls`）
   * ─────────────────────────────────────────────────────────────────────────
   * ★ 核心的 `PostControls.userControls` 是一个**空实现**（直接 `return new ItemList()`，
   *   见 vendor/flarum/core/js/src/forum/utils/PostControls.js L53-55），
   *   因为普通用户对自己的帖子本来就没有「用户级」操作。所以我们插进去的分享项
   *   是该位置上**唯一**的内容，不存在与核心项撞 key 的问题。
   * ★ `PostControls.controls()` 会按 user → moderation → destructive 三段拼装，
   *   并在每段之间自动补 `<Separator />`，所以分享项天然落在「编辑/删除」之上，
   *   分隔线不用我们自己加。
   * ───────────────────────────────────────────────────────────────────────── */
  extend(PostControls, 'userControls', function (items, post) {
    // 渲染门禁（2026-10-02）：本帖被隐藏、或所属讨论被整篇隐藏时不挂分享项（见 isHiddenEntity）
    if (isHiddenEntity(post) || isHiddenEntity(post.discussion())) return;
    items.add('jiedhShare', m(ShareButton, {
      resolve: () => postSource(post),
      config: config
    }), -10);
  });

  /* ─────────────────────────────────────────────────────────────────────────
   * 入口 ③：博客文章页正文下方（fof/blog 的 `BlogItem.articleItems`）
   * ─────────────────────────────────────────────────────────────────────────
   * ★ 用**字符串路径**形式的 extend（惰性模块机制）：
   *   BlogItem 是 fof/blog 的 `ext:` 模块，采用字符串路径后，模块已加载则立刻生效、
   *   尚未加载则排队等它到位再自动生效，与初始化顺序完全无关。
   *   （同款先例见 packages/media/js/src/forum/index.tsx L144 / L149。）
   *
   * ★ priority 90：核心 articleItems() 里正文是 100、评论区是 75
   *   （见 vendor/fof/blog/js/src/forum/pages/BlogItem.tsx L248 / L251），
   *   90 正好落在两者**之间** —— 读完正文、在评论之前看到分享条，是阅读场景的正确位置。
   *
   * ★ 形态用 `variant="bar"`（横向常驻分享条），不是下拉项：博客是长文阅读场景，
   *   需要一个「就在眼前」的显眼入口（§6.1.3 表格第 3 行的原话）。
   * ───────────────────────────────────────────────────────────────────────── */
  extend('ext:fof/blog/forum/pages/BlogItem', 'articleItems', function (items) {
    // 文章尚未加载完（`this.article` 为 null）时不渲染，避免闪出一个点了没反应的按钮
    if (!this.article) return;

    // 渲染门禁（2026-10-02）：被隐藏/下架的博客文章不挂分享条（见 isHiddenEntity）
    if (isHiddenEntity(this.article)) return;
    items.add('jiedhShare', m(ShareButton, {
      variant: "bar",
      resolve: () => blogSource(this.article),
      config: config
    }), 90);
  });

  /*
   * 关键日志（仅在 debug 下）：三处入口挂载完成。
   * 排查「入口没出现」类问题时，这一行能立刻区分「没挂上」与「挂上了但被样式/权限藏了」。
   */
  if (app.forum.attribute('debug')) {
    console.log('[jiedh-share] 三处分享入口已挂载（讨论页控件 / 楼层控件 / 博客正文下方）');
  }

  // P2-5：核心页（讨论/帖/博客）的 `?ref=share` 游客轻引导 —— 与三处入口同受 `enabled` 总开关约束
  mountGuestGuide();

  // P4-9：第 4 个挂载动作 —— 正文划选浮动气泡「分享选中内容」（§22.8 / §22.11）
  mountSelectionBubble(config);
}

/* ═══════════════════════════════════════════════════════════════════════════
 * P2-5 · `?ref=share` 游客轻引导（核心页侧）
 * ═══════════════════════════════════════════════════════════════════════════
 * 设计依据：doc/分享功能设计方案.md §6.9.3（L994-1006）。
 *   场景：访客从 P1 生成的分享链接（形如 `/d/123-slug?ref=share`）进入，
 *         此刻是**未登录**状态 ⇒ 在「页面底部」给一条**可关闭的轻提示**，
 *         引导他去注册，好一起讨论。
 *
 * 逐条对照 §6.9.3 的四项硬约束：
 *   ①「只显示一次」—— 用 `sessionStorage` 记住「已显示」，同一标签页内不再出现
 *      （选 session 而非 local：关掉标签页即忘，下次从分享链接进来仍值得再提醒一次，
 *       同时避免长期占位骚扰，符合「轻引导」的定位）。
 *   ②「不遮挡内容」—— 用核心 `app.alerts.show()`，其容器 `.AlertManager` 是
 *      `position: fixed; bottom: 20px; left: 20px`（见 vendor/flarum/core/less/common/AlertManager.less），
 *      **非模态、非遮罩**，只是贴在左下角的一个普通提示条，不阻断阅读。
 *   ③「不绕过 registration-guard」—— 「注册」按钮走 `app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/SignUpModal'))`，
 *      与核心 HeaderSecondary 的注册入口**同源同参**，不携带任何豁免字段，风控口径完全不变。
 *   ④「文案与样式与主站一致」—— 提示条本体是核心 Alert 组件（核心 token 自动生效），
 *      按钮用核心 `Button Button--primary` 类，**零新增 CSS、零新增体积**。
 *
 * ★ 关于 `flarum.reg.asyncModuleImport('flarum/forum/components/SignUpModal')` 的两个已核实前提（2026-09-25）：
 *   1. flarum-webpack-config 的 autoChunkNameLoader 会把这行 import() 自动改写成
 *      `flarum.reg.asyncModuleImport('flarum/forum/components/SignUpModal')`
 *      （见 node_modules/flarum-webpack-config/src/autoChunkNameLoader.cjs L45-87），
 *      因此**这行必须整行写完**（该 loader 按正则逐行匹配，跨行会漏改）。
 *   2. `ModalManagerState.show()` 支持异步类（vendor/.../states/ModalManagerState.ts L88
 *      会 `(await componentClass()).default`），`ExportRegistry.asyncModuleImport()`
 *      对核心惰性 chunk（SignUpModal 正是其一）也能正确加载并补 `.default`。
 *      用法与核心 HeaderSecondary.js L76-84 完全同款。
 * ───────────────────────────────────────────────────────────────────────── */

/** sessionStorage 键：记录「本标签页已展示过 ref 轻引导」，值 '1' 表示已展示 */
const REF_GUIDE_STORAGE_KEY = 'jiedh-share.ref-guide-seen';

/**
 * 读取 `ref` 查询参数（只认字面量 `share`，其余一律不触发）。
 * 用 try/catch 包住：极老旧浏览器或沙箱环境可能没有 URLSearchParams，
 * 此时当作「没有 ref」处理即可（轻引导本就是个锦上添花的功能，不能因此报错）。
 */
function refParamOf(location) {
  try {
    return new URLSearchParams(location.search).get('ref') ?? '';
  } catch {
    return '';
  }
}

/**
 * 是否在当前标签页已经展示过轻引导。
 * 读取失败（如隐私模式禁用 sessionStorage）时返回 `true` ——
 * 宁可少打扰一次，也不要在存不进标记的情况下反复弹。
 */
function hasSeenRefGuide() {
  try {
    return window.sessionStorage.getItem(REF_GUIDE_STORAGE_KEY) === '1';
  } catch {
    return true;
  }
}

/** 标记「已展示」，同样容忍存储不可用（写完即用，不关心持久化是否成功） */
function markRefGuideSeen() {
  try {
    window.sessionStorage.setItem(REF_GUIDE_STORAGE_KEY, '1');
  } catch {
    /* 存储不可用：忽略即可，功能降级为「每次都会展示」，不影响正确性 */
  }
}

/**
 * 核心页游客轻引导主流程。四道闸门全过才展示：
 *   ① 未登录（已登录用户看到「注册」毫无意义）
 *   ② 站点开放注册（`allowSignUp`，否则既没有可点的行动、也不该给出误导性提示）
 *   ③ URL 带 `?ref=share`（只服务分享链接带来的访客，站内自然浏览不打扰）
 *   ④ 本标签页尚未展示过
 */
function mountGuestGuide() {
  if (app.session.user) return;
  if (!app.forum.attribute('allowSignUp')) return;
  if (refParamOf(window.location) !== 'share') return;
  if (hasSeenRefGuide()) return;
  app.alerts.show({
    type: 'info',
    /*
     * ★★ `controls` 必须是**数组**（踩坑，2026-09-26 实测）：
     *   核心 `Alert.view()` 里写的是 `const controls = (extract(attrs,'controls') || []) as Mithril.Vnode[]`
     *   紧接着 `listItems(controls.concat(dismissControl))` —— 即核心**假定它是数组**。
     *   若这里传单个 vnode，`controls.concat` 直接抛
     *   `TypeError: controls.concat is not a function`；而该异常发生在
     *   `beforeMount` 阶段的首次 redraw 里 ⇒ 整个论坛 SPA 挂载失败，
     *   页面只剩核心的兜底文案「加载论坛时出错，请强制刷新页面重试。」
     *   （排查成本极高：控制台只有一行 minify 后的 `h.concat is not a function`。）
     * ⇒ 结论：**单元素也要包成数组** `[<button …/>]`。见 vendor/flarum/core/js/src/common/components/Alert.tsx L43 / L79。
     */
    controls: [
    // 按钮用核心 Button 类名而非 import Button 组件：省一次模块依赖，体积为零
    m("button", {
      type: "button",
      className: "Button Button--primary",
      onclick: () => app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/SignUpModal'))
    }, app.translator.trans('jiedh-share.forum.ref_guide_cta'))]
  }, app.translator.trans('jiedh-share.forum.ref_guide_text'));

  // 展示成功后再落标记（顺序刻意）：若上面因故抛错，至少不会把「已展示」永久写死，
  // 用户下一次带 ref 访问仍有机会看到引导
  markRefGuideSeen();
}

/* ═══════════════════════════════════════════════════════════════════════════
 * P4-9 · 正文划选浮动气泡（§22.8 / §22.11）
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * 气泡模块加载器（**必须单行书写**，见下方红线）。
 *
 * 仅在「首次 mouseup 且选区有效」时调用 ⇒ 首屏零成本（§22.11.1）。
 *
 * ⚠️ 注释里刻意不写动态 import 的代码形态：webpack 的 autoChunkNameLoader
 *    用正则扫描全部源码行且**不区分注释与代码**（§22.11.3 红线 2）。
 *    ⇒ 本文件所有动态 import 站点的注释都遵循同一写法，不要在任何一处写代码形态。
 */
const loadBubbleModule = () => import(/* webpackChunkName: 'forum/components/ShareSelectionBubble', webpackMode: 'lazy-once' */ './components/ShareSelectionBubble');

/* ─────────────────────────────────────────────────────────────────────────
 * ★ 2026-10-02（戒打卡分享重设计 §4.1.3 / §4.1.4）：跨扩展 API 的懒加载入口
 * ─────────────────────────────────────────────────────────────────────────
 * 两个 loader 都是**单行书写**的动态 import 站点（同上红线）。
 * 它们是「薄壳」的全部重逻辑来源：注册进 `window.jiedhShare` 的只是转发箭头函数，
 * 真正的面板 / 海报引擎要等调用时才下载 ⇒ 主包体积不受影响（§0.4 预算核算）。
 * ───────────────────────────────────────────────────────────────────────── */
const loadOpenPanelModule = () => import(/* webpackChunkName: 'forum/shared/openPanel', webpackMode: 'lazy-once' */ './shared/openPanel');
const loadRenderPosterModule = () => import(/* webpackChunkName: 'forum/shared/renderPoster', webpackMode: 'lazy-once' */ './shared/renderPoster');

/**
 * DOM 元素 → 分享原料（选区气泡的「source 解析」通道）。
 *
 * 两条映射通道（§22.8 / P4-9 实施期裁决 #10）：
 *   ① 讨论页：`.PostStream-item[data-id]` → `app.store.getById('posts', id)` → 既有 `postSource()`；
 *   ② 博客页：正文**没有** `.PostStream-item` 祖先（BlogItem 直接渲染 CommentPost）
 *      ⇒ 走 `resolveBlogArticle()` → 既有 `blogSource()`（取模型口径见该函数的「根因」注释）。
 *
 * ★ 为什么找到楼层就直接 return（不继续兜底博客通道）：
 *   在讨论页上若 `getById` 取不到（帖子尚未进 store），回退到博客通道只会返回 null，
 *   徒增一次无效查询且掩盖真实原因；直接 return null 让气泡安静不弹，语义更干净。
 */
function sourceFromElement(el) {
  const floor = el.closest('.PostStream-item');
  if (floor) {
    const postId = floor.getAttribute('data-id');
    return postId ? postSource(app.store.getById('posts', postId)) : null;
  }
  return blogSource(resolveBlogArticle());
}

/**
 * 取「当前博客文章页的 Discussion 模型」——**旧口径**（现作为兜底通道）。
 *
 * ★ 数据来源：`vendor/fof/blog/js/src/forum/pages/BlogItem.tsx` L118 的
 *   `app.current.set('discussion', article)` —— 已 Grep 全 vendor 确认这是**唯一**写入点。
 * ★ ⚠️ 但该写入点被包在 `if (this.near) { ... }` 块内（BlogItem.tsx L116-121），而
 *   `this.near = Number(m.route.param('near')) || 0`（同文件 L50）；本项目的博客 URL
 *   形如 `/blog/8/jie-dao-hang`（`near` 段是 slug 尾段）⇒ `Number('jie-dao-hang') || 0 === 0`
 *   ⇒ **写入被跳过** ⇒ 本函数在博客文章页恒返回 null。这正是「划选后气泡不出现」的根因
 *   （详见 `resolveBlogArticle()` 的说明与 doc/分享功能设计方案.md §22.16.2）。
 *   ⇒ 因此本函数**只作兜底**（未来若 fof/blog 修好写入点，这里也能取到）。
 * ★ `app.current` 是核心公开状态（`Application.tsx` L245 `current: PageState`），
 *   `PageState.get(key)` 返回 `any`；项目内先例：packages/nav/js/src/forum/index.ts L114。
 */
function currentBlogArticle() {
  const article = app.current.get('discussion');
  return article ?? null;
}

/**
 * 取「当前博客文章页的 Discussion 模型」——**修复口径**（store 优先 + 旧口径兜底）。
 *
 * ── 根因（doc/分享功能设计方案.md §22.16.2）─────────────────────────────────
 *   fof/blog 的文章页注册了两套路由（vendor/fof/blog/js/src/forum/extend.ts L23-24）：
 *       .add('blogArticle',      '/blog/:id',       BlogItem)
 *       .add('blogArticle.near', '/blog/:id/:near', BlogItem)
 *   `BlogItem.tsx` L50 用 `Number(m.route.param('near')) || 0` 解析 `near`，而项目里博客
 *   URL 的 `near` 段是 slug 尾段（如 `jie-dao-hang`）⇒ `near` 恒为 0 ⇒ `app.current.set
 *   ('discussion', article)` 所在的 `if (this.near)` 块被跳过 ⇒ `app.current` 上没有文章。
 *
 * ── 修复思路（不碰 vendor，只在既有「DOM → 分享原料」通道内换数据来源）──────────
 *   绕过被跳过的写入点，**直接从 store 取模型**：
 *     ① 用 `m.route.param('id')` 拿路由参数，再 `app.store.getById('discussions', id)`；
 *     ② id 归一：与 `BlogItem.tsx` L76 的 `m.route.param('id').split('-')[0]` **同口径**
 *        —— 基础变体 `/blog/8-jie-dao-hang` 下 `id` 参数是 `'8-jie-dao-hang'`，
 *        必须切掉 `-slug` 尾段才能命中 store 里的 `'8'`（live 实测已验证可完整取回文章）；
 *     ③ 页面类型门禁：仅当处于博客文章页（`routeName` 以 `blogArticle` 开头，由核心
 *        `Page.tsx` L36 写入 `app.current`）时才走 store 通道，避免在讨论页 `/d/:id`
 *        等路由上把普通 Discussion 误判成博客文章；
 *     ④ store 取不到时回落到旧口径 `currentBlogArticle()`（双通道冗余，未来核心行为
 *        若变化仍可用）。
 *
 * ★ 裸用全局 `m` 是项目既有惯例、**不违反「不 import mithril」红线**：`m` 由
 *   flarum-webpack-config（`pragma: 'm'`）注入且是运行期真全局，核心自身大量这样用
 *   （`Application.tsx` L469/L477/L609），fof/blog 的 `BlogItem.tsx` L75 亦然。
 */
function resolveBlogArticle() {
  // ③ 页面类型门禁：`routeName` 形如 `blogArticle` / `blogArticle.near`（核心 Page.tsx L36 写入）
  const routeName = String(app.current.get('routeName') ?? '');
  if (routeName.indexOf('blogArticle') === 0) {
    const routeId = m.route.param('id'); // ① 路由参数（全局 m，见上方注释）
    if (routeId) {
      // ② id 归一：`8-jie-dao-hang` → `8`（与 BlogItem.tsx L76 同口径）
      const fromStore = app.store.getById('discussions', String(routeId).split('-')[0]);
      if (fromStore) return fromStore;
    }
  }

  // ④ 兜底：旧口径（fof/blog 若修好写入点，这里也能取到）
  return currentBlogArticle();
}

/**
 * 挂载「正文划选浮动气泡」的第 4 处入口（§22.8 / §22.11）。
 *
 * ★ 本函数体是**主包唯一增量**（§22.11.2 估算 +250~400B 未压缩，T12 需实测取证）：
 *   只注册一个 document 级 `mouseup` 监听 + 首行的移动端/特性检测，
 *   真正的气泡逻辑全在惰性 chunk 里。
 *
 * 三条设计要点：
 *   ① **移动端不弹气泡**（§22.8.5，用户裁决）：`'ontouchstart' in window` 即返回。
 *      副作用（已登记取舍 #12）：带触摸屏的 Windows 笔记本也会被判定为「移动端」而
 *      不弹气泡 —— 降级路径是「点楼层分享按钮走整楼分享」，可接受。
 *   ② **D3 特性检测**：无 `window.getSelection` 的浏览器直接不注册（功能不可用但不报错）。
 *   ③ **惰性加载**：首次有效划选才拉 chunk；用 `handler` 变量做「已加载」标记，
 *      `loading` 变量防并发重复加载。
 *      ★ 顺序关键：必须先判 `handler` 再判选区，否则「点击空白处收起气泡」这条路径
 *        会因为选区已折叠而提前 return，气泡永远不消失。
 */
function mountSelectionBubble(config) {
  if ('ontouchstart' in window) return;
  if (typeof window.getSelection !== 'function') return;
  let handler = null;
  let loading = false;
  document.addEventListener('mouseup', event => {
    if (event.button !== 0) return;

    // 点击气泡自身：交给气泡自己的 onclick 处理，不能在这里 hide（否则点不动）
    // ⚠️ 类名与 ShareSelectionBubble.tsx 的 BUBBLE_ROOT_CLASS 保持一致（硬编码是
    //    为了让主包不必静态 import 气泡 chunk；两处改动必须同步）。
    const target = event.target;
    if (target && typeof target.closest === 'function' && target.closest('.jiedh-ShareSelectionBubble')) return;
    if (handler) {
      handler(event);
      return;
    }
    if (loading) return;

    // 廉价预判：无选区时不加载 chunk（把 chunk 下载推迟到真正划选之后）
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) return;
    loading = true;
    loadBubbleModule().then(mod => {
      handler = mod.default(config, sourceFromElement);
      handler(event); // 让「第一次划选」也能立刻弹气泡
    }).catch(error => {
      loading = false; // 复位以便下次划选重试
      console.warn('[jiedh-share] 选区气泡 chunk 加载失败（本次划选不弹气泡，可重试）：', error);
    });
  });
}

/* ═══════════════════════════════════════════════════════════════════════════
 * 跨扩展公开 API：`window.jiedhShare`（★ 2026-10-02 戒打卡分享重设计 §4.1）
 * ═══════════════════════════════════════════════════════════════════════════
 * 用途：本项目各扩展前端**独立 webpack 编译**、无法互相 import（见 shared/types.ts 文件头），
 *   戒打卡（jiedh/checkin）要复用 share 的面板与海报引擎，只能经由这个全局薄壳。
 *
 * 三条硬约束（§4.1.3）：
 *   ① **薄壳**：这里只允许出现「转发箭头函数 + 版本号 + 能力探测」，
 *      不得出现任何绘制 / 图标 / 文案逻辑 —— 它们全在懒加载的 chunk 里；
 *   ② **幂等**：已存在且版本不低于本实现时直接返回，绝不覆盖（热重载 / 多入口安全）；
 *   ③ **不暴露内部工具**：channels / palettes / qr 等内部模块一律不挂 window。
 *
 * ★ 契约字段版本号：仅在**破坏性**调整 `JiedhShareApi` 既有字段时才 +1。
 *   新增可选字段不算破坏性变更，不需要 +1。
 * ═══════════════════════════════════════════════════════════════════════════ */

/** 本实现提供的契约版本（与 `JiedhShareApi.version` 同源，比较时用数值比较） */
const JIEDH_SHARE_API_VERSION = '1';

/**
 * 能力探测：目前两个 primitive 都恒可用。
 *
 * 之所以仍做成函数而不是直接给 `true`：将来若海报引擎在某些环境（如无 Canvas 的
 * 极端浏览器）不可用，可以只改这一处而**不动契约形状**（§4.1.3 的降级要求）。
 */
function canUseJiedhShare(name) {
  if (name === 'open') return true;
  if (name === 'renderPoster') return true;
  return false;
}

/**
 * 注册 `window.jiedhShare`（幂等）。
 *
 * @param config 前台配置快照。**由闭包捕获并在每次 `open()` 时重新读取最新值**：
 *               面板打开期间管理员改动设置的概率极低，但重新读一次的成本为 0，
 *               且能避免「热更新后仍用旧配置」的困惑。
 *               ⚠️ 刻意不在这里 import 具体的 config 对象构造函数，避免任何循环依赖。
 */
function registerJiedhShareApi(config) {
  if (typeof window === 'undefined') return;
  const existing = window.jiedhShare;

  /*
   * 幂等判定：已存在且**版本不低于**本实现 ⇒ 保持不动。
   * 用 `Number()` 比较而不是字符串比较（'10' < '9' 是字符串比较的经典陷阱）。
   * 脏值（非数字）按 0 处理 ⇒ 会被本实现覆盖，属于期望行为。
   */
  if (existing && Number(existing.version) >= Number(JIEDH_SHARE_API_VERSION)) {
    return;
  }
  const api = {
    version: JIEDH_SHARE_API_VERSION,
    can: canUseJiedhShare,
    /*
     * 打开分享面板：动态加载 openPanel chunk 后转交。
     * `config` 用闭包捕获 —— 这样 openPanel.ts 不必反向 import 本文件（避免循环依赖）。
     */
    open: descriptor => loadOpenPanelModule().then(mod => mod.openPanel(descriptor, config)),
    /* 离屏渲染海报：动态加载 renderPoster chunk 后转交（一次返回 blob + canvas）。 */
    renderPoster: spec => loadRenderPosterModule().then(mod => mod.renderPoster(spec))
  };
  window.jiedhShare = api;
  if (app.forum.attribute('debug')) {
    console.log('[jiedh-share] 已注册跨扩展 API window.jiedhShare（version ' + api.version + '）');
  }
}

/*
 * initializer 只负责「登记」——把挂载动作排到 `beforeMount` 队列里。
 * 它本身不读 `app.forum` / `app.session`（见上方 mountShareEntries 的时序说明），
 * 因此无论如何都不会在 boot 阶段抛错。
 */
app.initializers.add('jiedh-share', () => {
  app.beforeMount(mountShareEntries);
});flarum.reg.addChunkModule('999', '773', 'jiedh-share', 'forum/components/ShareSelectionBubble');
flarum.reg.addChunkModule('999', '773', 'jiedh-share', 'forum/utils/text');
flarum.reg.addChunkModule('457', '363', 'jiedh-share', 'forum/shared/openPanel');
flarum.reg.addChunkModule('162', '584', 'jiedh-share', 'forum/shared/renderPoster');